import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, ArrowLeft, Building2, Mail, Phone, MapPin, Calendar,
  DollarSign, Target, Users, FileText, MessageSquare, Settings, Globe,
  ClipboardList, ClipboardCheck, FolderOpen, TrendingUp, Clock, Tag,
  ExternalLink, Edit3, CheckCircle, AlertCircle, Star, ChevronRight,
  Activity, BarChart3, Plus, RefreshCw, Send, Sparkles, User, X,
  Eye, Instagram, Facebook, Megaphone
} from 'lucide-react';
import { useStaffAuth } from '../context/StaffAuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ============ MAIN CLIENT PROFILE PAGE ============
export default function ClientProfilePage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { staffUser } = useStaffAuth();
  
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchClient();
  }, [clientId]);

  const fetchClient = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/staff/clients/${clientId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setClient(data.client);
      } else {
        setError('Client not found');
      }
    } catch (err) {
      console.error('Error fetching client:', err);
      setError('Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
          <p className="text-slate-500">Loading client profile...</p>
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800 mb-2">{error || 'Client Not Found'}</h2>
          <p className="text-slate-500 mb-4">The client you're looking for doesn't exist or you don't have access.</p>
          <button
            onClick={() => navigate('/staff-d')}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Plan tier configuration
  const getTierConfig = (tier) => {
    const tierLower = (tier || 'growth').toLowerCase();
    switch (tierLower) {
      case 'starter':
        return {
          gradient: 'from-slate-500 via-slate-600 to-slate-700',
          badge: 'bg-slate-400/20 text-slate-100 border-slate-400/30',
          label: 'Starter',
          icon: '🌱'
        };
      case 'partner':
        return {
          gradient: 'from-amber-500 via-yellow-500 to-orange-500',
          badge: 'bg-amber-400/20 text-amber-100 border-amber-400/30',
          label: 'Partner',
          icon: '👑'
        };
      case 'growth':
      default:
        return {
          gradient: 'from-blue-500 via-blue-600 to-indigo-600',
          badge: 'bg-blue-400/20 text-blue-100 border-blue-400/30',
          label: 'Growth',
          icon: '🚀'
        };
    }
  };

  const tierConfig = getTierConfig(client.plan_tier);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'tasks', label: 'Tasks', icon: ClipboardList },
    { id: 'approvals', label: 'Approvals', icon: ClipboardCheck },
    { id: 'ads', label: 'Ads & Campaigns', icon: Target },
    { id: 'landing-page', label: 'Landing Page', icon: Globe },
    { id: 'files', label: 'Files', icon: FolderOpen },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Back Button and Breadcrumb */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/staff-d')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div className="flex items-center gap-2 text-sm">
                <Link to="/staff-d" className="text-slate-500 hover:text-slate-700">Dashboard</Link>
                <ChevronRight className="w-4 h-4 text-slate-400" />
                <span className="text-slate-800 font-medium">{client.business_name}</span>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Message
              </button>
              <button className="px-4 py-2 text-sm font-medium bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Call
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Client Hero Section */}
      <div className={`bg-gradient-to-r ${tierConfig.gradient} relative overflow-hidden`}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOGM5Ljk0MSAwIDE4LTguMDU5IDE4LTE4cy04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNHMxNCA2LjI2OCAxNCAxNHMtNi4yNjggMTQtMTQgMTR6IiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDUpIi8+PC9nPjwvc3ZnPg==')] opacity-20" />
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 py-8 relative">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            {/* Client Avatar */}
            <div className="w-24 h-24 md:w-32 md:h-32 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white text-4xl md:text-5xl font-bold border border-white/30 shadow-xl">
              {client.business_name?.charAt(0) || 'C'}
            </div>

            {/* Client Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-2xl md:text-3xl font-bold text-white">{client.business_name}</h1>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${
                  client.client_status === 'active' 
                    ? 'bg-green-500/20 text-green-100 border-green-400/30' 
                    : 'bg-amber-500/20 text-amber-100 border-amber-400/30'
                }`}>
                  {client.client_status === 'active' ? '✓' : '⏸'} {client.client_status || 'Active'}
                </span>
              </div>

              <p className="text-white/80 mb-4">{client.short_id} • {client.industry || 'Business'}</p>

              <div className="flex flex-wrap gap-4 text-white/90 text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>{client.primary_contact_email || 'No email'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>{client.primary_contact_phone || 'No phone'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>{client.city || 'Location'}{client.state ? `, ${client.state}` : ''}</span>
                </div>
              </div>
            </div>

            {/* Plan Badge */}
            <div className="text-right">
              <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                <span>Plan</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{tierConfig.icon}</span>
                <span className="text-3xl font-bold text-white">{tierConfig.label}</span>
              </div>
              <div className="text-white/80 text-lg mt-1">
                ${client.monthly_fee || 0}/mo
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <QuickStat icon={Users} label="Total Leads" value="-" />
            <QuickStat icon={ClipboardCheck} label="Pending Approvals" value="-" />
            <QuickStat icon={MessageSquare} label="Unread Messages" value="-" />
            <QuickStat icon={Calendar} label="Client Since" value={client.start_date ? new Date(client.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A'} />
          </div>
        </div>
      </div>

      {/* Quick Action Links */}
      <div className="bg-white border-b border-slate-200 py-3">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center gap-3 overflow-x-auto pb-1">
            <QuickLink icon={Globe} label="Google Ads" url="#" color="blue" />
            <QuickLink icon={Facebook} label="Facebook Ads" url="#" color="indigo" />
            <QuickLink icon={BarChart3} label="Analytics" url="#" color="purple" />
            <QuickLink icon={FileText} label="Reports" url="#" color="green" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-30">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-blue-500 bg-blue-50/50'
                    : 'text-slate-600 border-transparent hover:text-slate-800 hover:bg-slate-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'overview' && <OverviewTab client={client} setActiveTab={setActiveTab} />}
            {activeTab === 'leads' && <LeadsTab client={client} />}
            {activeTab === 'tasks' && <PlaceholderTab title="Tasks" description="Client tasks and work queue" icon={ClipboardList} />}
            {activeTab === 'approvals' && <PlaceholderTab title="Approvals" description="Pending approvals and sign-offs" icon={ClipboardCheck} />}
            {activeTab === 'ads' && <PlaceholderTab title="Ads & Campaigns" description="Ad performance and campaign management" icon={Target} />}
            {activeTab === 'landing-page' && <PlaceholderTab title="Landing Page" description="Landing page builder and analytics" icon={Globe} />}
            {activeTab === 'files' && <PlaceholderTab title="Files" description="Client files and documents" icon={FolderOpen} />}
            {activeTab === 'messages' && <PlaceholderTab title="Messages" description="Client communication history" icon={MessageSquare} />}
            {activeTab === 'settings' && <SettingsTab client={client} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ============ QUICK STAT COMPONENT ============
function QuickStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
      <div className="flex items-center gap-2 text-white/70 text-sm mb-1">
        <Icon className="w-4 h-4" />
        <span>{label}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

// ============ QUICK LINK COMPONENT ============
function QuickLink({ icon: Icon, label, url, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200',
    indigo: 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border-indigo-200',
    purple: 'bg-purple-50 text-purple-600 hover:bg-purple-100 border-purple-200',
    green: 'bg-green-50 text-green-600 hover:bg-green-100 border-green-200',
  };

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${colors[color]}`}
    >
      <Icon className="w-4 h-4" />
      {label}
      <ExternalLink className="w-3 h-3 opacity-50" />
    </a>
  );
}

// ============ OVERVIEW TAB ============
function OverviewTab({ client, setActiveTab }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Content Column */}
      <div className="lg:col-span-2 space-y-6">
        {/* Performance Overview */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            Performance Overview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <div className="text-2xl font-bold text-slate-800">-</div>
              <div className="text-xs text-slate-500">Total Leads</div>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <div className="text-2xl font-bold text-slate-800">-</div>
              <div className="text-xs text-slate-500">This Month</div>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <div className="text-2xl font-bold text-slate-800">-</div>
              <div className="text-xs text-slate-500">Conversion Rate</div>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <div className="text-2xl font-bold text-slate-800">-</div>
              <div className="text-xs text-slate-500">Avg. Deal Value</div>
            </div>
          </div>
          <p className="text-sm text-slate-500 text-center mt-4 italic">
            Connect analytics integrations to see live data
          </p>
        </div>

        {/* Recent Leads */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-green-500" />
              Recent Leads
            </h3>
            <button 
              onClick={() => setActiveTab('leads')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View All
            </button>
          </div>
          <div className="text-center py-8 text-slate-500">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm">Switch to the Leads tab to view pipeline</p>
          </div>
        </div>

        {/* Work Queue */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-amber-500" />
              Work Queue
            </h3>
            <button 
              onClick={() => setActiveTab('tasks')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View All
            </button>
          </div>
          <div className="text-center py-8 text-slate-500">
            <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm">No pending tasks</p>
          </div>
        </div>
      </div>

      {/* Sidebar Column */}
      <div className="space-y-6">
        {/* Contact Information */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-500" />
            Primary Contact
          </h3>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-slate-500">Name</div>
              <div className="font-medium text-slate-800">
                {client.primary_contact_first_name} {client.primary_contact_last_name}
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-500">Email</div>
              <a href={`mailto:${client.primary_contact_email}`} className="font-medium text-blue-600 hover:underline">
                {client.primary_contact_email}
              </a>
            </div>
            <div>
              <div className="text-sm text-slate-500">Phone</div>
              <a href={`tel:${client.primary_contact_phone}`} className="font-medium text-blue-600 hover:underline">
                {client.primary_contact_phone}
              </a>
            </div>
          </div>
        </div>

        {/* Account Details */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-500" />
            Account Details
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Client ID</span>
              <span className="font-mono text-sm text-slate-800">{client.short_id}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Plan</span>
              <span className="font-medium text-slate-800 capitalize">{client.plan_tier}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Monthly Fee</span>
              <span className="font-medium text-slate-800">${client.monthly_fee}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Start Date</span>
              <span className="text-slate-800">
                {client.start_date ? new Date(client.start_date).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Status</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                client.client_status === 'active' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {client.client_status || 'Active'}
              </span>
            </div>
          </div>
        </div>

        {/* Internal Notes */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-500" />
            Internal Notes
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full ml-auto">Staff Only</span>
          </h3>
          <div className="text-center py-4 text-slate-500">
            <p className="text-sm">No notes yet</p>
            <button className="text-sm text-blue-600 hover:underline mt-2">
              Add a note
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ LEADS TAB ============
function LeadsTab({ client }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('pipeline');
  const [showAddModal, setShowAddModal] = useState(false);
  const [stats, setStats] = useState({ total: 0, byStage: {} });

  const STAGES = [
    { id: 'new_lead', label: 'New Lead', color: '#3b82f6' },
    { id: 'attempting_contact', label: 'Attempting Contact', color: '#f59e0b' },
    { id: 'contacted', label: 'Contacted', color: '#8b5cf6' },
    { id: 'site_visit_scheduled', label: 'Site Visit Scheduled', color: '#10b981' },
    { id: 'estimate_sent', label: 'Estimate Sent', color: '#ec4899' },
    { id: 'won', label: 'Won', color: '#22c55e' },
    { id: 'lost', label: 'Lost', color: '#ef4444' }
  ];

  useEffect(() => {
    fetchLeads();
  }, [client.client_id]);

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API}/api/crm/leads?client_id=${client.client_id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
        
        const byStage = {};
        (data.leads || []).forEach(lead => {
          byStage[lead.pipeline_stage] = (byStage[lead.pipeline_stage] || 0) + 1;
        });
        setStats({ total: data.total || 0, byStage });
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStage = async (leadId, newStage, lostReason = null) => {
    try {
      const body = { pipeline_stage: newStage };
      if (lostReason) body.lost_reason = lostReason;

      const res = await fetch(`${API}/api/crm/leads/${leadId}/stage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (res.ok) {
        fetchLeads();
      }
    } catch (err) {
      console.error('Error updating stage:', err);
    }
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('leadId');
    const currentStage = e.dataTransfer.getData('currentStage');
    
    if (leadId && currentStage !== targetStage) {
      if (targetStage === 'lost') {
        const reason = window.prompt('Please enter a reason for marking this lead as lost:');
        if (reason) updateLeadStage(leadId, targetStage, reason);
      } else {
        updateLeadStage(leadId, targetStage);
      }
    }
  };

  const getLeadsByStage = (stageId) => leads.filter(l => l.pipeline_stage === stageId);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Leads Pipeline</h2>
          <p className="text-slate-500">{stats.total} total leads for {client.business_name}</p>
        </div>
        <div className="flex gap-3">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('pipeline')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'pipeline' ? 'bg-white shadow text-slate-800' : 'text-slate-500'
              }`}
            >
              Pipeline
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'list' ? 'bg-white shadow text-slate-800' : 'text-slate-500'
              }`}
            >
              List
            </button>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl transition-colors flex items-center gap-2"
            data-testid="add-lead-btn"
          >
            <Plus className="w-4 h-4" />
            Add Lead
          </button>
        </div>
      </div>

      {/* Stage Stats */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {STAGES.slice(0, 5).map(stage => (
          <div 
            key={stage.id}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl min-w-fit"
          >
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
            <span className="text-sm text-slate-600">{stage.label}</span>
            <span className="text-sm font-bold" style={{ color: stage.color }}>
              {stats.byStage[stage.id] || 0}
            </span>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-2xl">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="font-semibold text-slate-800 mb-2">No leads yet</h3>
          <p className="text-slate-500 text-sm mb-4">Add leads manually or they'll appear here from your campaigns</p>
          <button 
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl"
          >
            Add First Lead
          </button>
        </div>
      ) : viewMode === 'pipeline' ? (
        /* Pipeline View - Constrained to fit screen */
        <div className="overflow-x-auto">
          <div className="flex gap-2 min-w-max pb-2">
            {STAGES.map((stage) => {
              const stageLeads = getLeadsByStage(stage.id);
              return (
                <div 
                  key={stage.id}
                  className="flex-shrink-0 w-48 bg-slate-50 rounded-xl p-2"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, stage.id)}
                >
                  <div className="flex items-center gap-1.5 mb-2 px-1">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                    <span className="font-medium text-slate-700 text-xs truncate">{stage.label}</span>
                    <span className="ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ 
                      backgroundColor: `${stage.color}15`, 
                      color: stage.color 
                    }}>
                      {stageLeads.length}
                    </span>
                  </div>
                  
                  <div className="space-y-1.5 max-h-80 overflow-y-auto">
                    {stageLeads.length === 0 ? (
                      <div className="text-center text-slate-400 text-xs py-3">No leads</div>
                    ) : (
                      stageLeads.map((lead) => (
                        <div
                          key={lead.lead_id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('leadId', lead.lead_id);
                            e.dataTransfer.setData('currentStage', lead.pipeline_stage);
                          }}
                          className="bg-white rounded-lg p-2 border border-slate-200 cursor-move hover:shadow-md transition-shadow"
                        >
                          <div className="font-medium text-slate-800 text-xs truncate">
                            {lead.first_name} {lead.last_name}
                          </div>
                          <div className="text-xs text-slate-500 truncate">{lead.phone}</div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs font-medium text-green-600">
                              ${lead.deal_value?.toLocaleString() || '0'}
                            </span>
                            <span className="text-xs text-slate-400">{formatDate(lead.created_at)}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Stage</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Value</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {leads.map((lead) => (
                <tr key={lead.lead_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{lead.first_name} {lead.last_name}</div>
                    <div className="text-sm text-slate-500">{lead.email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 capitalize">{lead.source?.replace('_', ' ')}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 capitalize">
                      {lead.pipeline_stage?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-green-600">
                    ${lead.deal_value?.toLocaleString() || '0'}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">{formatDate(lead.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddModal && (
        <AddLeadModal 
          client={client} 
          onClose={() => setShowAddModal(false)} 
          onSuccess={() => { setShowAddModal(false); fetchLeads(); }} 
        />
      )}
    </div>
  );
}

// ============ ADD LEAD MODAL ============
function AddLeadModal({ client, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    source: 'direct',
    deal_value: '',
    service_type: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const response = await fetch(`${API}/api/crm/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: client.client_id,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email || undefined,
          phone: formData.phone,
          source: formData.source,
          deal_value: formData.deal_value ? parseFloat(formData.deal_value) : undefined,
          service_type: formData.service_type || undefined
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create lead');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-lg font-semibold text-slate-800">Add New Lead</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
              <input
                type="text"
                required
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
              <input
                type="text"
                required
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
              <select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              >
                <option value="direct">Direct</option>
                <option value="google_ads">Google Ads</option>
                <option value="facebook_ads">Facebook Ads</option>
                <option value="instagram_ads">Instagram</option>
                <option value="referral">Referral</option>
                <option value="website_form">Website Form</option>
                <option value="phone_call">Phone Call</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Deal Value ($)</label>
              <input
                type="number"
                value={formData.deal_value}
                onChange={(e) => setFormData({ ...formData, deal_value: e.target.value })}
                placeholder="0"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Service Type</label>
            <input
              type="text"
              value={formData.service_type}
              onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
              placeholder="e.g., Roof Repair"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.first_name || !formData.last_name || !formData.phone}
              className="flex-1 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white rounded-xl font-medium flex items-center justify-center gap-2"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Lead
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ============ SETTINGS TAB ============
function SettingsTab({ client }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Business Information */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-blue-500" />
          Business Information
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-slate-500 mb-1">Business Name</label>
            <div className="font-medium text-slate-800">{client.business_name}</div>
          </div>
          <div>
            <label className="block text-sm text-slate-500 mb-1">Industry</label>
            <div className="font-medium text-slate-800">{client.industry || 'Not specified'}</div>
          </div>
          <div>
            <label className="block text-sm text-slate-500 mb-1">Website</label>
            <div className="font-medium text-slate-800">{client.website || 'Not specified'}</div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-green-500" />
          Contact Information
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-slate-500 mb-1">Primary Contact</label>
            <div className="font-medium text-slate-800">
              {client.primary_contact_first_name} {client.primary_contact_last_name}
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-500 mb-1">Email</label>
            <div className="font-medium text-slate-800">{client.primary_contact_email}</div>
          </div>
          <div>
            <label className="block text-sm text-slate-500 mb-1">Phone</label>
            <div className="font-medium text-slate-800">{client.primary_contact_phone}</div>
          </div>
        </div>
      </div>

      {/* Billing */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-amber-500" />
          Billing
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Plan Tier</span>
            <span className="font-medium text-slate-800 capitalize">{client.plan_tier}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Monthly Fee</span>
            <span className="font-medium text-slate-800">${client.monthly_fee}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Status</span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              client.client_status === 'active' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-amber-100 text-amber-700'
            }`}>
              {client.client_status || 'Active'}
            </span>
          </div>
        </div>
      </div>

      {/* Portal Access */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-purple-500" />
          Portal Access
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-slate-500 mb-1">Login ID</label>
            <div className="font-mono text-slate-800">{client.short_id}</div>
          </div>
          <div>
            <label className="block text-sm text-slate-500 mb-1">Portal URL</label>
            <a href="/portal/login" target="_blank" className="text-blue-600 hover:underline text-sm">
              {window.location.origin}/portal/login
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ PLACEHOLDER TAB ============
function PlaceholderTab({ title, description, icon: Icon }) {
  return (
    <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
      <Icon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-slate-800 mb-2">{title}</h3>
      <p className="text-slate-500">{description}</p>
      <p className="text-sm text-slate-400 mt-4">Coming soon...</p>
    </div>
  );
}
